import { forwardRef, HTMLAttributes, useMemo } from 'react'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useContrastedBackground } from 'app/frontend/base/components/Card'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useCharacterKeystones } from '../hooks/useCharacterKeystones'

interface Props {
  character?: wowaudit.Character
  weeksAgo: number
  minKeyLevel?: number
}

const KeystoneTotal = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { label: string; value: number }
>(({ label, value, className, ...props }, ref) => {
  const background = useContrastedBackground()

  return (
    <div
      ref={ref}
      className={cn(
        background,
        'border rounded-lg flex items-center gap-2 px-2 py-0.5',
        className,
      )}
      {...props}
    >
      <span className='text-xs text-muted-foreground font-semibold'>
        {label}
      </span>
      <span className='text-sm leading-tight font-medium'>{value}</span>
    </div>
  )
})
KeystoneTotal.displayName = 'KeystoneTotal'

const KeystoneTotals = ({ character, weeksAgo, minKeyLevel }: Props) => {
  const { weekTotal, seasonTotal, seasonDungeonTotals, isLoaded } =
    useCharacterKeystones(character, weeksAgo, minKeyLevel)
  const {
    data: { currentSeason },
  } = usePageInfo()

  const dungeons = useMemo(
    () =>
      [...currentSeason.keystone_dungeons].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [currentSeason.keystone_dungeons],
  )

  return (
    <span
      className={cn(
        'flex items-center gap-3 transition-opacity duration-300',
        isLoaded ? 'opacity-100' : 'opacity-0',
      )}
    >
      <Popover>
        <PopoverTrigger asChild>
          <KeystoneTotal
            label='Season'
            value={seasonTotal}
            className='cursor-pointer transition-colors hover:border-primary/40'
          />
        </PopoverTrigger>
        <PopoverContent align='start' className='w-72 p-2'>
          <div className='mb-1 flex items-center gap-2 px-1 text-xs font-semibold text-muted-foreground'>
            <span className='flex-1'>Dungeon</span>
            <span className='w-8 text-right'>Runs</span>
            <span className='w-10 text-right'>Best</span>
          </div>
          <div className='flex flex-col gap-0.5'>
            {dungeons.map((dungeon) => {
              const total = seasonDungeonTotals[dungeon.id]
              return (
                <div
                  key={dungeon.id}
                  className='flex items-center gap-2 rounded-md px-1 py-0.5'
                >
                  <img
                    src={`https://render.worldofwarcraft.com/eu/zones/${dungeon.background}-small.jpg`}
                    alt={dungeon.name}
                    className='h-6 w-6 rounded border'
                  />
                  <span className='flex-1 truncate text-sm'>
                    {dungeon.name}
                  </span>
                  <span className='w-8 text-right text-sm tabular-nums'>
                    {total?.count ?? 0}
                  </span>
                  <span className='w-10 text-right text-sm tabular-nums'>
                    {total?.highestLevel ? `+${total.highestLevel}` : '-'}
                  </span>
                </div>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
      <KeystoneTotal label='Week' value={weekTotal} />
    </span>
  )
}

export default KeystoneTotals
